import { Claim, Destination, Item, Participant, Session, SessionEvent } from './types';
import { firstName, formatRwf, momoDialString } from '../splitReceipt/model';

/** Units a line splits into: its quantity when whole ("3× Primus"), else one. */
export function lineUnits(item: Pick<Item, 'quantity'>) {
  const q = item.quantity;
  return Number.isInteger(q) && q >= 1 ? q : 1;
}

/** Splits [amount] by [weights], the parts adding up exactly (largest remainder). */
export function allocate(amount: number, weights: number[]): number[] {
  const total = weights.reduce((a, b) => a + Math.max(0, b), 0);
  if (weights.length === 0 || amount === 0 || total <= 0) return weights.map(() => 0);
  const sign = amount < 0 ? -1 : 1;
  const value = Math.abs(amount);
  const parts = weights.map((w) => Math.floor((value * Math.max(0, w)) / total));
  let left = value - parts.reduce((a, b) => a + b, 0);
  const order = weights
    .map((w, i) => ({ i, r: (value * Math.max(0, w)) % total }))
    .sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of order) {
    if (left === 0) break;
    if (weights[i] <= 0) continue;
    parts[i] += 1;
    left -= 1;
  }
  return parts.map((p) => p * sign);
}

/**
 * The line rule, the same as the server's: with U units claimed of a line of
 * quantity q, each pays total × units / q while U ≤ q (the rest is unclaimed),
 * and the line is split by units / U once more are claimed than exist. Parts in
 * [units] order.
 */
export function splitLine(item: Item, units: number[]): number[] {
  const claimed = units.reduce((a, b) => a + b, 0);
  const rest = Math.max(0, lineUnits(item) - claimed);
  return allocate(item.totalPriceMinor, [...units, rest]).slice(0, units.length);
}

export type Owner = { participant: Participant; units: number };

/** Everyone on an item, with how many of the line they had. */
export function ownersOf(session: Session, itemId: string): Owner[] {
  const owners: Owner[] = [];
  for (const participant of session.participants) {
    const claim = participant.claims.find((c) => c.receiptItemId === itemId);
    if (claim && claim.units > 0) owners.push({ participant, units: claim.units });
  }
  return owners;
}

export function participantOf(session: Session | null, id: string | null | undefined) {
  return session && id ? session.participants.find((p) => p.id === id) ?? null : null;
}

export function unitsOf(participant: Participant | null, itemId: string) {
  return participant?.claims.find((c) => c.receiptItemId === itemId)?.units ?? 0;
}

/** [claims] with [units] of [itemId]: 0 takes it off. */
export function withUnits(claims: Claim[], itemId: string, units: number): Claim[] {
  const rest = claims.filter((c) => c.receiptItemId !== itemId);
  return units > 0 ? [...rest, { receiptItemId: itemId, units }] : rest;
}

/** [session] as it will be once [participantId]'s picks are [claims]: shown while they're sent. */
export function withClaimsOf(session: Session, participantId: string, claims: Claim[]): Session {
  return {
    ...session,
    participants: session.participants.map((p) => (p.id === participantId ? { ...p, claims } : p)),
  };
}

/** A payer's own picks are set while a payment of theirs is pending or confirmed. */
export function picksLocked(participant: Participant) {
  return participant.paymentState !== 'none';
}

/**
 * What [me] would pay for an item if they had [units] of it, before charges,
 * next to everyone else already on it.
 */
export function quote(session: Session, item: Item, me: string, units: number): number {
  const others = ownersOf(session, item.id).filter((o) => o.participant.id !== me);
  const parts = splitLine(item, [...others.map((o) => o.units), units]);
  return parts[parts.length - 1] ?? 0;
}

/** "Bob", "Bob & Carol", "Bob, Carol +2". */
export function joinNames(names: string[]) {
  return names.length <= 2 ? names.join(' & ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

/** First names of everyone else on an item ("You" is left out). */
export function othersOn(session: Session, itemId: string, me: string) {
  return ownersOf(session, itemId)
    .filter((o) => o.participant.id !== me)
    .map((o) => firstName(o.participant.displayName));
}

/** The sharer: whoever the split's money goes to on "send to me". */
export function hostName(session: Session) {
  const host = session.participants.find((p) => p.role === 'host');
  return firstName(host?.displayName ?? session.destination?.name ?? 'The sharer');
}

/**
 * What someone on the split should hear about a change they didn't make:
 * someone sharing their item, or the sharer fixing the receipt. Null otherwise.
 */
export function liveNotice(event: SessionEvent, me: string): string | null {
  const session = event.session;
  if (event.type === 'receipt_updated') {
    return `${hostName(session)} updated the receipt. Shares are worked out again.`;
  }
  if (event.type !== 'item_shared' || !event.changedByParticipantId || event.changedByParticipantId === me) {
    return null;
  }
  const mine = new Set(participantOf(session, me)?.claims.map((c) => c.receiptItemId) ?? []);
  const items = (event.receiptItemIds ?? [])
    .filter((id) => mine.has(id))
    .map((id) => session.receipt.items.find((i) => i.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  if (items.length === 0) return null;
  const who = firstName(participantOf(session, event.changedByParticipantId)?.displayName ?? 'Someone');
  const share = participantOf(session, me)?.money?.shareMinor;
  return `${who} is sharing ${joinNames(items)} with you${
    share == null ? '' : ` · your total is now ${formatRwf(share)}`
  }`;
}
/** MoMo dial string for paying the split's destination. */
export function destinationDial(destination: Destination, amount: number) {
  return momoDialString(
    {
      type: destination.type === 'vendor' ? 'merchant' : 'creator',
      name: destination.name ?? '',
      identifier: destination.identifier,
    },
    amount,
  );
}

/** MoMo dial string for sending [amount] to a person. */
export function personDial(phoneNumber: string, amount: number) {
  return momoDialString({ type: 'creator', name: '', identifier: phoneNumber }, amount);
}

/** Who this browser is on a split. */
export type Guest = { participantId: string; participantToken: string };

const guestKey = (shareToken: string) => `pebla.splitSession.${shareToken}`;

export function loadGuest(shareToken: string, storage: Storage = window.localStorage): Guest | null {
  try {
    const raw = storage.getItem(guestKey(shareToken));
    if (!raw) return null;
    const guest = JSON.parse(raw);
    return typeof guest?.participantId === 'string' && typeof guest?.participantToken === 'string' ? guest : null;
  } catch {
    return null;
  }
}

export function saveGuest(shareToken: string, guest: Guest, storage: Storage = window.localStorage) {
  try {
    storage.setItem(guestKey(shareToken), JSON.stringify(guest));
  } catch {
    /* private mode: they'll join again next time */
  }
}

export function clearGuest(shareToken: string, storage: Storage = window.localStorage) {
  try {
    storage.removeItem(guestKey(shareToken));
  } catch {
    /* nothing kept */
  }
}
